import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@avanta/shared/components/Text';
import { inputVariants } from '@avanta/shared/components/ui/Input';
import { Label } from '@avanta/shared/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@avanta/shared/components/ui/Popover';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { formatDateSimple } from '@avanta/shared/utils/date';
import { Calendar } from '../Calendar';
import { DatePickerController } from './controller';
import { type DatePickerProps } from './types';

const DatePicker = ({
  label,
  helperText,
  required,
  placeholder,
  className,
  variant = 'filled',
  value,
  onChange,
  disabled = false,
  readOnly = false,
  calendarProps,
}: DatePickerProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const [isOpen, setIsOpen] = useState(false);
  const isHelperTextVisible = Boolean(helperText?.trim());
  const resolvedPlaceholder = placeholder ?? t('Common.datePicker.placeholder');

  const handleSelect = (date: Date | undefined): void => {
    onChange?.(date);
    if (date) setIsOpen(false);
  };

  return (
    <div
      className="group/form notranslate flex w-full flex-col gap-2"
      translate="no"
      data-variant={variant}
    >
      {label && <Label required={required}>{label}</Label>}
      <Popover open={isOpen && !readOnly} onOpenChange={readOnly ? undefined : setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-slot="input"
            disabled={disabled}
            data-readonly={readOnly}
            className={cn(
              inputVariants({ variant }),
              'flex items-center justify-between gap-2 text-left font-normal',
              !value && 'bg-gray-100 border-teal-300',
              readOnly &&
                'cursor-default border-white bg-mint-100 text-denim-800 focus-visible:bg-mint-100',
              'notranslate',
              className
            )}
            translate="no"
          >
            <span className={cn('truncate', !value && 'text-gray-400')}>
              {value ? formatDateSimple(value) : resolvedPlaceholder}
            </span>
            {readOnly ? null : <CalendarIcon className="size-4 shrink-0 text-teal-600" />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            selected={value}
            onSelect={handleSelect}
            defaultMonth={value}
            captionLayout="dropdown"
            {...calendarProps}
          />
        </PopoverContent>
      </Popover>
      {isHelperTextVisible && (
        <Text
          tag="span"
          variant="text-sm-light"
          className="group-data-[variant=invalid]/form:text-red-500"
        >
          {helperText}
        </Text>
      )}
    </div>
  );
};

export { DatePicker, DatePickerController, DatePickerProps };
